import React, { useEffect, useState } from 'react';
import { Box, Button, Heading } from 'lucid-components-react';

import {
  Drupal_FieldParagraphFlexiStyledMultiSectionsFieldSections,
  Drupal_ParagraphFlexiSection
} from 'generated/graphql';
import { LucidTheme } from 'types/types';

interface Props {
  entities: Drupal_FieldParagraphFlexiStyledMultiSectionsFieldSections[];
}

const slideshowDelayMs = 4000;
const slideshowClickPauseMs = 8000;

let interval: number;

const ThreeSlideshow = ({ entities }: Props) => {
  const [activeEntityIndex, setActiveEntityIndex] = useState(0);
  const [pauseInterval, setPauseInterval] = useState(false);

  useEffect(() => {
    if (!pauseInterval) {
      interval = window.setInterval(() => {
        let nextIndex = activeEntityIndex + 1;
        if (nextIndex >= 3) {
          nextIndex = 0;
        }
        setActiveEntityIndex(nextIndex);
      }, slideshowDelayMs);
    } else {
      window.setTimeout(() => {
        setPauseInterval(false);
      }, slideshowClickPauseMs);
    }

    return () => {
      window.clearInterval(interval);
    };
  }, [activeEntityIndex, pauseInterval]);

  const handleClick = (i: number) => {
    setActiveEntityIndex(i);
    setPauseInterval(true);
  };

  return (
    <>
      <Box display={{ default: 'block', tablet: 'none' }} mt={9}>
        {entities.map(({ entity }) => {
          const {
            fieldFlexiHeader,
            fieldFlexiImage
          } = entity as Drupal_ParagraphFlexiSection;

          return (
            <Box key={fieldFlexiHeader} mb={9}>
              <Heading
                css={(theme: LucidTheme) => ({
                  paddingLeft: theme.space[5],
                  paddingRight: theme.space[5],
                  position: 'relative',
                  '&::before': {
                    content: '"→"',
                    position: 'absolute',
                    top: -theme.space[4],
                    left: theme.space[5]
                  }
                })}
                variant="subheadXXS700"
              >
                {fieldFlexiHeader}
              </Heading>
              <Box mx={-11}>
                <img alt={fieldFlexiHeader!} src={fieldFlexiImage!} />
              </Box>
            </Box>
          );
        })}
      </Box>
      <Box
        display={{ default: 'none', tablet: 'block' }}
        mb={8}
        mt={9}
        position="relative"
        width="100%"
      >
        <Box pb="52%">
          {entities.map(({ entity }, i) => {
            const thisEntity = entity as Drupal_ParagraphFlexiSection;
            return (
              <img
                key={thisEntity.fieldFlexiImage!}
                alt={thisEntity.fieldFlexiHeader!}
                css={{
                  position: 'absolute',
                  top: 0,
                  left: 0,
                  right: 0,
                  opacity: i === activeEntityIndex ? 1 : 0,
                  transition: 'opacity 2s',
                  maxHeight: '620px'
                }}
                src={thisEntity.fieldFlexiImage!}
              />
            );
          })}
        </Box>
        <Box display="flex" justifyContent="space-between">
          {entities.map(({ entity }, i) => {
            const { fieldFlexiHeader } = entity as Drupal_ParagraphFlexiSection;

            return (
              <Heading
                key={fieldFlexiHeader}
                as="p"
                css={{ flex: 1, textAlign: 'center' }}
                variant="subheadXS700"
              >
                <Button
                  asLink
                  css={(theme: LucidTheme) => ({
                    fontWeight: 700,
                    color:
                      i === activeEntityIndex
                        ? theme.colors.primary
                        : 'inherit',
                    position: 'relative',
                    width: 'min-content',
                    wordSpacing: '100vw',
                    '&::before': {
                      content: '"→"',
                      position: 'absolute',
                      top: 0,
                      left: -theme.space[5]
                    }
                  })}
                  onClick={() => handleClick(i)}
                >
                  {fieldFlexiHeader}
                </Button>
              </Heading>
            );
          })}
        </Box>
      </Box>
    </>
  );
};

export default ThreeSlideshow;
